// 顧客アカウントの API (#1138): ログイン/セッション/プロフィール/退会/ウォレット/
// カード/お気に入り/チャージ。配置ルールは index.ts のヘッダ参照。
import { API_BASE, apiFetch, trackedFetch } from './_client'
import type { VariationSnapshot } from '../../lib/menuVariation'
import type { RefundVoucher } from './reservations'

// #1168 Web領収書 / #1207 Webレシート (明細+EMV) の共通レスポンス
export type WebReceiptData = {
  transactionId: string
  slipNo: string
  amountJpy: number
  taxJpy: number
  // #1171 税率別の内訳 (税率の高い順)。適格請求書は税率ごとの区分を求める。
  // 単一税率の店では 1 行。
  taxRows?: Array<{ rate: number; subtotal: number; tax: number }>
  diningLocation?: 'eat_in' | 'takeout'
  refunded: boolean
  paidAt: string | null
  paymentMethod: string | null
  cardBrand: string | null
  cardLast4: string | null
  items: Array<{
    name?: string; nameEn?: string; price?: number; quantity?: number
    /** #2031 選んだバリエーション。紙のレシートと同じものを出す */
    variations?: VariationSnapshot[] | null
  }>
  memo: string | null
  // #1217 店頭注文 (カウンター受け取り) のみ: 呼び出し番号と受け取り状況ページ URL
  callNumber?: string | null
  statusUrl?: string | null
  paymentNo: string | null
  authorizationCode: string | null
  cardReceipt: {
    applicationPreferredName?: string
    dedicatedFileName?: string
    accountType?: string
    authorizationResponseCode?: string
  } | null
  store: {
    name: string | null
    address: string | null
    phone: string | null
    /** #1881 店舗ロゴ (カラーの元画像。紙と違い 2 値化は不要) */
    logoUrl?: string | null
    /** #1880 建物名・階数。住所本体の下に別行で出す (紙のレシートと同じ) */
    building?: string | null
    invoiceRegistrationNumber: string | null
    /** #1484 「レシート」は飲食の語。体験でこの語を出さないための判定に使う */
    genre?: 'experience' | 'food' | 'souvenir' | 'other' | null
  }
}

export const authApi = {
  // ---- #1230 Web Push (自分の端末の通知購読) ----
  getPushVapidKey: async (): Promise<{ publicKey: string | null }> => {
    const res = await apiFetch('/push/vapid-public-key')
    return res.json()
  },
  registerPushSubscription: async (subscription: unknown, locale: string) => {
    const res = await apiFetch('/push/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subscription, locale }),
    })
    return res.json()
  },
  removePushSubscription: async (endpoint: string) => {
    const res = await apiFetch('/push/unsubscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint }),
    })
    return res.json()
  },

  // #1384 Phase 2「最後に判明した位置」を購読へ記録する。
  // ブラウザはアプリを閉じている間に位置を取れないので、分かったときに記録しておく。
  // 呼び元はアプリ起動時 (GPS 許可済み) と、決済・スタンプ取得 (店舗の座標。**許可不要**)。
  reportPushLocation: async (lat: number, lng: number) => {
    const res = await apiFetch('/push/location', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lat, lng }),
    })
    return res.json() as Promise<{ updated: number }>
  },

  // お知らせ push の購読トピック。**業務通知 (#1230 の呼び出し) とは別の同意**。
  // 混ぜると、案内をうるさく感じた人が通知ごと切り、呼び出しまで届かなくなる。
  getPushTopics: async () => {
    const res = await apiFetch('/push/topics')
    return res.json() as Promise<{ topics: string[]; subscribed: boolean }>
  },

  setPushTopics: async (topics: string[]) => {
    const res = await apiFetch('/push/topics', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ topics }),
    })
    return res.json() as Promise<{ updated: number; topics: string[] }>
  },

  // Auth
  login: async (provider: string, name: string, email: string, walletAddress?: string) => {
    const res = await apiFetch('/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ provider, name, email, wallet_address: walletAddress || null }),
    })
    return res.json()
  },

  // Session restore (returns user + wallet + transactions + cards)
  // #1849 **user_id はもう送らない。** サーバはトークンから引く。
  // 以前はクエリの user_id で誰の氏名・メール・取引履歴・カードでも読めた。
  // #1641 注文のリアルタイム購読のために customerId をトークンへ載せる。
  // ゲストは X-Guest-Id を apiFetch が自動で付ける (未ログイン、または #1922 で
  // 匿名トークンを引数で渡したとき)。
  syncRealtimeClaim: async (idToken?: string) => {
    // #1922 トークンは apiFetch の引数で渡す。headers に載せると既定アプリの
    // ログイン (オーナー等) のトークンで上書きされ、ゲストの購読が始まらない。
    const res = await apiFetch('/auth/realtime-claim', { method: 'POST' }, undefined, idToken)
    return res.json() as Promise<{ customerId: string; updated: boolean }>
  },

  getSession: async () => {
    const res = await apiFetch('/auth/session')
    return res.json()
  },

  // Profile
  /**
   * プロフィールの部分更新。**渡したものだけ変わる。**
   *
   * #2005 以前は name と nationality が必須で、紙レシートや年代だけを変えるときも
   * 画面が持っている値を送り返していた。手元に無ければ 'US' を送っていたため、
   * **関係のない設定を変えただけで国籍が書き換わりうる**作りだった。
   */
  updateProfile: async (
    userId: string,
    opts?: {
      name?: string
      /** '' = 未回答に戻す */
      nationality?: string
      /** #2005 実際に言語を選んだときだけ送る (既定値を焼かない) */
      language?: string
      nickname?: string
      displayPreference?: 'tag' | 'name' | 'nickname'
      // #1207 紙レシート設定 (アプリ決済時に店頭で自動印刷するか)。undefined = 変更しない
      wantsPaperReceipt?: boolean
      // #1274 セグメント推薦用の任意属性。undefined = 変更しない、'' = 未回答に戻す
      ageBand?: string
      gender?: string
      // #2004 居住地 (任意)。**必ず 2 つ一組で送る** (サーバは片方だけを断る)。
      // undefined = 変更しない、'' と '' = 未回答に戻す
      residencePrefecture?: string
      residenceCity?: string
      // #2004 地域のお知らせメールの停止 (true = 停止)。undefined = 変更しない
      residenceMailOptOut?: boolean
    },
  ) => {
    const res = await apiFetch('/auth/update-profile', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: opts?.name,
        nationality: opts?.nationality,
        language: opts?.language,
        nickname: opts?.nickname,
        displayPreference: opts?.displayPreference,
        wantsPaperReceipt: opts?.wantsPaperReceipt,
        ageBand: opts?.ageBand,
        gender: opts?.gender,
        residencePrefecture: opts?.residencePrefecture,
        residenceCity: opts?.residenceCity,
        residenceMailOptOut: opts?.residenceMailOptOut,
      }),
    }, userId)
    return res.json()
  },

  // #2004 都道府県の市区町村の一覧 (居住地の入力欄が、県を選んだ時に読む)。公開・ログイン不要。
  // マスタの正はサーバ。全国を画面に持たせず、選んだ県の分だけ受け取る。
  getMunicipalities: async (prefecture: string) => {
    const res = await apiFetch(`/geo/municipalities?prefecture=${encodeURIComponent(prefecture)}`)
    return res.json() as Promise<{ prefecture: string; cities: string[] }>
  },

  // Account deletion
  deleteAccount: async (userId: string) => {
    const res = await apiFetch('/auth/delete-account', { method: 'DELETE' }, userId)
    return res.json()
  },

  // Transactions
  // #863 **user_id はもう送らない。** サーバはトークンから引く。
  // 以前はクエリの user_id で誰の履歴でも読めた (#54 のレガシー fallback)。
  getTransactions: async () => {
    const res = await apiFetch('/wallet/transactions')
    return res.json()
  },
  // #2308 自分の会計の返金の引換券 (取引の履歴からの返金がカードに返せなかったとき。本人だけ)
  getMySaleRefundVoucher: async (transactionId: string) => {
    const res = await apiFetch(`/wallet/transactions/${encodeURIComponent(transactionId)}/refund-voucher`)
    return res.json() as Promise<RefundVoucher>
  },

  // #1433 スポンサーポイント: コードを入力して受け取る
  redeemSponsorCode: async (code: string) => {
    const res = await apiFetch('/sponsor-points/redeem', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    })
    return res.json() as Promise<{
      sponsor_id: string
      sponsor_name: string
      amount_jpy: number
      expires_at: string
      new_balance: number
    }>
  },

  // #1433 発行体ごとの残高。**発行体ごとに使える店が違う**ので 1 つの数字に纏められない
  getSponsorPoints: async () => {
    const res = await apiFetch('/sponsor-points/mine')
    return res.json() as Promise<Array<{ id: string; name: string; balance_jpy: number }>>
  },

  // #1188 Phase 2: 失効日別の内訳を返す (残高カードタップで開く modal で使用)
  getExpiryBreakdown: async () => {
    const res = await apiFetch('/wallet/expiry-breakdown')
    return res.json() as Promise<{
      lots: Array<{
        charge_id: string
        /** **残額**。発行額ではない（一部使った分は減っている） */
        amount_jpy: number
        charged_at: string
        expires_at: string
        days_remaining: number
        /**
         * #1444 有償/無償。**区分表示は法的要件** — 無償ポイントは前払式支払手段に
         * 該当しないが、「利用者から見て明確に区別できること」を満たさないと
         * 有償分と一緒に規制対象になる。画面に必ず出す。
         */
        paid: boolean
        /** 原資の出し手（self / yokoso_campaign / city_xxx ...） */
        issuer: string
        /** 発行額。残額との差が「使った分」 */
        issued_amount_jpy: number
      }>
      next_expiring_at: string | null
      next_expiring_amount_jpy: number | null
      total_active_jpy: number
    }>
  },

  // #1168 Web領収書: 宛名・但し書き付き領収書ページのデータ (本人のみ)。
  getWebReceipt: async (transactionId: string) => {
    const res = await apiFetch(`/wallet/transactions/${transactionId}/receipt`)
    if (!res.ok) throw new Error('Failed to load receipt')
    return res.json() as Promise<WebReceiptData>
  },

  // #1207 公開レシート: 店側の決済完了 QR から開く (HMAC トークン認可、ログイン不要)。
  getPublicReceipt: async (transactionId: string, token: string) => {
    const res = await trackedFetch(
      `${API_BASE}/wallet/receipts/${transactionId}/public?t=${encodeURIComponent(token)}`,
    )
    if (!res.ok) throw new Error('Failed to load receipt')
    return res.json() as Promise<WebReceiptData>
  },

  // Cards
  // #863 GET /wallet/cards は削除した (どこからも呼ばれておらず、認証も無かった)。
  // 再び必要になったら `Depends(verify_customer_token)` を付けて生やす。

  addCard: async (userId: string, card: { brand: string; last4: string; expiry: string; name: string; is_default: boolean }) => {
    const res = await apiFetch('/wallet/cards', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(card),
    }, userId)
    return res.json()
  },

  removeCard: async (userId: string, cardId: string) => {
    const res = await apiFetch(`/wallet/cards/${cardId}`, { method: 'DELETE' }, userId)
    return res.json()
  },

  setDefaultCard: async (userId: string, cardId: string) => {
    const res = await apiFetch(`/wallet/cards/${cardId}/default`, { method: 'PUT' }, userId)
    return res.json()
  },

  // Favorites (saved stores) — doc id = storeId, so PUT/DELETE are idempotent
  getFavorites: async (userId: string) => {
    const res = await apiFetch('/favorites', {}, userId)
    return res.json()
  },

  addFavorite: async (
    userId: string,
    storeId: string,
    // #1661 genre = 業態（飲食以外は category を持たない）
    snapshot: { name: string; name_en: string; image_url: string; category?: string; genre?: string },
  ) => {
    const res = await apiFetch(`/favorites/${storeId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(snapshot),
    }, userId)
    return res.json()
  },

  removeFavorite: async (userId: string, storeId: string) => {
    const res = await apiFetch(`/favorites/${storeId}`, { method: 'DELETE' }, userId)
    return res.json()
  },

  // Charge
  createChargeIntent: async (amount: number, mode?: string, channel?: 'revolut') => {
    const res = await apiFetch('/charge/create-intent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // #1188 Phase 2: mode 未指定は 'mock' (backend default)、
      // 'bank_transfer_jp' は Stripe customer_balance PaymentIntent を発行して
      // 振込先 Zengin 口座を返す (客が振込 → GET /charge/intent の照合で残高反映)。
      // #1764 channel は履歴に出す導線の区別 (Revolut 画面からの振込のみ)。
      // undefined の key は JSON.stringify が落とす。
      body: JSON.stringify({ amount_jpy: amount, mode, channel }),
    })
    return res.json()
  },

  executeCharge: async (chargeId: string) => {
    const res = await apiFetch('/charge/execute', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ charge_id: chargeId }),
    })
    return res.json()
  },

  // #1188 Phase 2: bank_transfer_jp の status poll 用。
  // 客が振込 → この endpoint が Stripe の PaymentIntent を照合して残高反映し、
  // intent が pending → executed に遷移する (#1764。webhook は登録されていない)。
  // Frontend が polling して executed 検知したら成功画面へ。
  getChargeIntentStatus: async (chargeId: string) => {
    const res = await apiFetch(`/charge/intent/${chargeId}`)
    return res.json()
  },

  // #1764 支払い済みなのに未反映のチャージを拾う (ウォレットを開いたときに呼ぶ)。
  // 振込画面を閉じた後に入金した分は誰も polling しないので、ここで Stripe と突き合わせる。
  reconcilePendingCharges: async (): Promise<{
    credited: string[]
    pending: string[]
    balance_jpy: number
  }> => {
    const res = await apiFetch('/charge/reconcile-pending', { method: 'POST' })
    return res.json()
  },

  // #1190 Phase 4: 残高不足時 auto top-up + 全額支払を単一 backend endpoint で実行。
  // 実運用の非同期 top-up と噛み合わないため、demo mode 限定 (backend は APP_ENV=prod
  // で 404 を返す safety net あり)。
  executePaymentWithTopup: async (request: {
    store_id: string
    amount_jpyc: number
    topup_amount: number
    topup_fee_jpyc: number
  }) => {
    const res = await apiFetch('/payment/execute-with-topup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    })
    return res.json()
  },

  // #1188 Phase 2: demo mode 限定で bank_transfer_jp の入金到達を人工的に発火。
  // 本来 Stripe webhook 経由で credit される流れを、Stripe CLI 無しで再現する。
  // backend が prod で 404 を返す safety net があるが、UI 側でも demo 判定で
  // ボタン自体を非表示にしてるので二重 gating。
  simulateBankTransferComplete: async (chargeId: string) => {
    const res = await apiFetch(
      `/debug/simulate-bank-transfer-complete/${chargeId}`,
      { method: 'POST' },
    )
    return res.json()
  },

  // Debug (demo mode only)
  //
  // #1942 **apiFetch を使う (認証ヘッダを付ける)。** この endpoint はロットを
  // 消すようになったので、サーバ側で「自分自身か、デモの共有ユーザーにしか
  // 効かない」ようにした。トークンを付けないと、ログイン中の人の残高設定が
  // 403 になる。
  resetBalance: async (
    userId: string,
    newBalance: number,
  ): Promise<{ status?: string; user_id?: string; new_balance?: number; error?: string }> => {
    if (import.meta.env.VITE_DEMO_MODE !== 'true') return { error: 'debug endpoints disabled' }
    const res = await apiFetch(
      `/debug/reset-balance?user_id=${encodeURIComponent(userId)}&new_balance=${newBalance}`,
      { method: 'POST' },
    )
    return res.json()
  },

  syncBalance: async (userId: string, chainBalance: number, reason?: string) => {
    if (import.meta.env.VITE_DEMO_MODE !== 'true') return { error: 'debug endpoints disabled' }
    const res = await trackedFetch(`${API_BASE}/debug/sync-balance`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, chain_balance: chainBalance, reason }),
    })
    return res.json()
  },
}
